/* ══════════════════════════════════════════════════════════
   NARRATE STEP — signification audio sub-step
   Thin wrapper over AudioRecorderSurface with allowMultipleClips=false
   so the signification flows (Tension / Triad) record a single clip
   per response. Visual/UX language now lives in AudioRecorderSurface.
   ══════════════════════════════════════════════════════════ */
import React, { useEffect, useState } from 'react';
import { useAudioRecorder } from '../audio/useAudioRecorder';
import { AudioRecorderSurface, AudioSurfaceState } from '../audio/AudioRecorderSurface';
import { AUDIO } from '../../../../utils/audioFeedback';

const DEFAULT_PROMPT = 'Why did you choose this point?';

interface NarrateStepProps {
  prompt: string;
  required: boolean;
  /** Single-clip audio. (Previously also passed a `narrative` string, but
      the text-input UI was a long-standing stub — removed.) */
  onSubmit: (audioBlob: Blob | null) => void;
  onBack: () => void;
}

const BackArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

export const NarrateStep: React.FC<NarrateStepProps> = ({ prompt, required, onSubmit, onBack }) => {
  const recorder = useAudioRecorder();
  const [state, setState] = useState<AudioSurfaceState>('ready');
  const displayPrompt = prompt || DEFAULT_PROMPT;
  const latestClip = recorder.clips[recorder.clips.length - 1] ?? null;

  // If user deletes the only clip in review, fall back to ready.
  useEffect(() => {
    if (state === 'review' && recorder.clips.length === 0) {
      setState('ready');
    }
  }, [state, recorder.clips.length]);

  const handleStart = async () => {
    AUDIO.tap();
    await recorder.startRecording();
    AUDIO.start();
    setState('recording');
  };

  const handlePause = () => {
    AUDIO.tap();
    recorder.pauseRecording();
    setState('paused');
  };

  const handleResume = () => {
    AUDIO.tap();
    recorder.resumeRecording();
    setState('recording');
  };

  const handleStop = (opts?: { discard?: boolean }) => {
    AUDIO.stop();
    recorder.stopRecording(opts);
    // Single-clip mode: a discarded too-short clip leaves us with nothing,
    // so stay on `ready`. Otherwise advance to review.
    setState(opts?.discard ? 'ready' : 'review');
  };

  const handleRetry = async () => {
    recorder.clearError();
    await handleStart();
  };

  const handleContinue = () => {
    if (required && !latestClip) return;
    AUDIO.success();
    onSubmit(latestClip?.blob ?? null);
  };

  return (
    <AudioRecorderSurface
      state={state}
      prompt={displayPrompt}
      duration={recorder.duration}
      audioLevel={recorder.audioLevel}
      frequencyData={recorder.frequencyData}
      clips={recorder.clips}
      allowMultipleClips={false}
      onStart={handleStart}
      onPause={handlePause}
      onResume={handleResume}
      onStop={handleStop}
      onDeleteClip={recorder.deleteClip}
      onContinue={handleContinue}
      error={recorder.error}
      onRetry={handleRetry}
      wasAutoPaused={recorder.wasAutoPaused}
      headerLeft={
        <button
          className="ars-close"
          onClick={onBack}
          aria-label="Back to placement"
          type="button"
        >
          <BackArrowIcon />
        </button>
      }
    />
  );
};
